<template>
  <div class="pledge-form-container">
    <div class="pledge-header">
      <h3>Pledge Form</h3>
    </div>

    <div class="pledge-scroll-area">
      <!-- 1. Donor Information -->
      <div class="form-section">
        <h4>1. Donor Information</h4>
        <div class="form-group">
          <label>Full Name *</label>
          <input type="text" v-model="form.fullName" placeholder="Enter your full name" />
        </div>
        <div class="form-row">
          <div class="form-group half">
            <label>Age</label>
            <input type="number" v-model="form.age" placeholder="Age" />
          </div>
          <div class="form-group half">
            <label>Gender</label>
            <CustomDropdown 
              v-model="form.gender" 
              :options="genderOptions"
              labelKey="label"
              valueKey="value"
              placeholder="Select Gender"
              :searchable="false"
            />
          </div>
        </div>
        <div class="form-group">
          <label>Email Address</label>
          <input type="email" v-model="form.email" placeholder="Enter email address" />
        </div>
        <div class="form-group">
          <label>Phone / WhatsApp *</label>
          <input type="tel" v-model="form.phone" placeholder="Enter phone number" />
        </div>
      </div>

      <!-- 2. Pledge Type -->
      <div class="form-section">
        <h4>2. Pledge Type</h4>
        <div class="checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.pledgeTypes" value="One-time" />
            <span class="custom-check"></span>
            One-time donation
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.pledgeTypes" value="Monthly recurring" />
            <span class="custom-check"></span>
            Monthly recurring donation
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.pledgeTypes" value="Six-monthly recurring" />
            <span class="custom-check"></span>
            Six-monthly recurring donation
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.pledgeTypes" value="Annual recurring" />
            <span class="custom-check"></span>
            Annual recurring donation
          </label>
        </div>
      </div>

      <!-- 3. Country & Currency -->
      <div class="form-section">
        <h4>3. Country & Currency</h4>
        <div class="form-row">
          <div class="form-group half">
            <label>Country *</label>
            <CustomDropdown 
              v-model="form.country" 
              :options="countries"
              labelKey="name"
              valueKey="name"
              placeholder="Select Country"
              @change="updateCurrency"
            />
          </div>
          <div class="form-group half">
            <label>City</label>
            <CustomDropdown 
              v-model="form.city" 
              :options="cities"
              labelKey="label"
              valueKey="value"
              :placeholder="isLoadingCities ? 'Loading cities...' : 'Select City'"
              :searchable="true"
              :clearable="true"
              :disabled="!form.country || isLoadingCities"
            />
          </div>
        </div>
        <div class="form-group">
          <label>Currency</label>
          <input type="text" v-model="form.currency" placeholder="e.g. USD, PKR" />
        </div>
      </div>

      <!-- 4. Donation Amount -->
      <div class="form-section">
        <h4>4. Donation Amount</h4>
        <div class="amount-presets">
          <button 
            v-for="(amount, index) in convertedPresets" 
            :key="index"
            :class="{ active: form.amount === amount && !form.isCustomAmount }"
            @click="selectAmount(presets[index])"
          >
            {{ currencySymbol }}{{ amount }}
          </button>
        </div>
        <div class="form-group custom-amount">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.isCustomAmount" />
            <span class="custom-check"></span>
            <span style="font-weight: 600; margin-left: 5px;">Custom amount</span>
          </label>
          <input 
            v-if="form.isCustomAmount" 
            type="number" 
            v-model="form.customAmount"
            @input="handleCustomAmountInput"
            placeholder="Enter amount" 
          />
        </div>
      </div>

      <!-- 5. Payment Method -->
      <div class="form-section">
        <h4>5. Payment Method</h4>
        <div class="checkbox-group">
          <label class="checkbox-label">
            <input type="radio" v-model="form.paymentMethod" value="Bank Transfer" />
            <span class="custom-radio"></span>
            Bank transfer in Pakistani account
          </label>
          <label class="checkbox-label">
            <input type="radio" v-model="form.paymentMethod" value="Online" />
            <span class="custom-radio"></span>
            Online through Muslim Giving
          </label>
        </div>
      </div>

      <!-- 6. Consent -->
      <div class="form-section">
        <h4>6. Consent</h4>
        <label class="checkbox-label">
          <input type="checkbox" v-model="form.consent" />
          <span class="custom-check"></span>
          I confirm this donation is voluntary *
        </label>
      </div>
      
      <button class="submit-btn" :disabled="!isValid" @click="submitPledge">
        Submit Pledge
      </button>

      <SuccessPopup 
        :visible="showSuccess" 
        title="Success!"
        message="Thank you for your pledge. May Allah accept your intention and reward you abundantly. Our team will contact you shortly."
        @close="handleSuccessClose"
      />

    </div>
  </div>
</template>

<script>
import CustomDropdown from './CustomDropdown.vue';
import SuccessPopup from './SuccessPopup.vue';
import { countries } from '../data/countries.js';
import { exchangeRates } from '../data/rates.js';

export default {
  name: "PledgeForm",
  components: {
    CustomDropdown,
    SuccessPopup
  },
  data() {
    return {
      showSuccess: false,
      form: {
        fullName: "",
        age: "",
        gender: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        pledgeTypes: [],
        currency: "USD",
        amount: 50,
        customAmount: "",
        isCustomAmount: false,
        paymentMethod: "",
        consent: false
      },
      usdAmount: 50,
      usdCustomAmount: "",
      liveRates: { ...exchangeRates },
      genderOptions: [
        { label: "Male", value: "male" },
        { label: "Female", value: "female" },
        { label: "Other", value: "other" }
      ],
      countries: countries,
      cities: [],
      isLoadingCities: false,
      presets: [25, 50, 100, 250]
    };
  },
  async created() {
    await this.fetchLiveRates();
  },
  computed: {
    isValid() {
      return (
        this.form.fullName &&
        this.form.phone &&
        this.form.country &&
        this.form.consent
      );
    },
    convertedPresets() {
      const rate = this.liveRates[this.form.currency] || 1;
      return this.presets.map(p => Math.round(p * rate));
    },
    currencySymbol() {
      const symbols = {
        'USD': '$',
        'PKR': 'Rs. ',
        'EUR': '€',
        'GBP': '£',
        'INR': '₹',
        'AED': 'AED ',
        'SAR': 'SAR ',
        'JPY': '¥',
        'CAD': 'CA$',
        'CNY': '¥',
        'AUD': 'A$',
        'CHF': 'CHF ',
        'TRY': '₺',
        'BDN': 'BDN ',
        'KWD': 'KWD ',
        'QAR': 'QAR ',
        'OMR': 'OMR ',
        'BHD': 'BD ',
        'EGP': 'EGP ',
        'MYR': 'RM ',
        'SGD': 'S$',
        'NZD': 'NZ$'
      };
      return symbols[this.form.currency] || (this.form.currency + ' ');
    }
  },
  methods: {
    async fetchLiveRates() {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await response.json();
        if (data && data.rates) {
          this.liveRates = { ...this.liveRates, ...data.rates };
          // PKR will be updated from 280 (fallback) to real-time (~279)
          
          // Re-calculate current amount after rates load
          this.updateCurrency({ currency: this.form.currency });
        }
      } catch (error) {
        console.error("Failed to fetch live rates, using fallback:", error);
      }
    },
    async fetchCities() {
      if (!this.form.country) {
        this.cities = [];
        return;
      }

      this.isLoadingCities = true;
      this.cities = [];
      this.form.city = ""; // Reset city selection

      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/cities', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            country: this.form.country
          })
        });

        const result = await response.json();
        
        if (!result.error && result.data) {
          this.cities = result.data.map(city => ({ label: city, value: city }));
        } else {
          console.warn("No cities found or error:", result.msg);
          this.cities = [];
        }
      } catch (error) {
        console.error("Failed to fetch cities:", error);
        this.cities = []; // Fallback to empty or consider allowing manual entry
      } finally {
        this.isLoadingCities = false;
      }
    },
    updateCurrency(countryData) {
      if (countryData && countryData.currency) {
        this.form.currency = countryData.currency;
        const rate = this.liveRates[this.form.currency] || 1;
        
        if (!this.form.isCustomAmount) {
          this.form.amount = Math.round(this.usdAmount * rate);
        } else if (this.usdCustomAmount) {
          this.form.customAmount = Math.round(this.usdCustomAmount * rate);
        }
      } else {
        // Reset to default (USD) if country is cleared
        this.form.currency = "USD";
        if (!this.form.isCustomAmount) {
          this.form.amount = this.usdAmount;
        } else if (this.usdCustomAmount) {
          this.form.customAmount = Math.round(this.usdCustomAmount);
        }
        this.form.city = "";
        this.cities = [];
      }
      
      this.fetchCities();
    },
    selectAmount(usdVal) {
      const rate = this.liveRates[this.form.currency] || 1;
      this.usdAmount = usdVal;
      this.form.amount = Math.round(usdVal * rate);
      this.form.isCustomAmount = false;
    },
    handleCustomAmountInput() {
      const rate = this.liveRates[this.form.currency] || 1;
      if (this.form.customAmount) {
        this.usdCustomAmount = this.form.customAmount / rate;
      } else {
        this.usdCustomAmount = "";
      }
    },
    submitPledge() {
      // Show success popup immediately for better UX
      this.showSuccess = true;

      const timestamp = new Date().toLocaleString();
      const adminEmail = 'contact@aafiamovement.info';
      const websiteUrl = 'https://www.aafiamovement.com/';
      
      // 1. Prepare Data for Email & Background API
      const pledgeData = {
        Id: 0,
        FullName: this.form.fullName,
        Age: parseInt(this.form.age) || 0,
        Gender: this.form.gender || "Not Specified",
        Email: this.form.email || "N/A",
        Phone: this.form.phone,
        Country: this.form.country,
        City: this.form.city || "N/A",
        Currency: this.form.currency,
        PledgeTypes: this.form.pledgeTypes,
        Amount: parseFloat(this.form.amount),
        IsCustomAmount: this.form.isCustomAmount,
        CustomAmount: parseFloat(this.form.customAmount) || 0,
        PaymentMethod: this.form.paymentMethod,
        Consent: this.form.consent
      };

      // Detailed payload for Admin Email
      const adminDetails = `
Pledge Form Submission Details:
-------------------------------
Full Name: ${pledgeData.FullName}
Age: ${pledgeData.Age || 'N/A'}
Gender: ${pledgeData.Gender}
Email: ${pledgeData.Email}
Phone / WhatsApp: ${pledgeData.Phone}
Location: ${pledgeData.City}, ${pledgeData.Country}

Pledge Type: ${pledgeData.PledgeTypes.join(', ')}
Amount: ${this.currencySymbol}${pledgeData.Amount}
Currency: ${pledgeData.Currency}
Payment Method: ${pledgeData.PaymentMethod}
Consent: ${pledgeData.Consent ? 'Confirmed' : 'Not Confirmed'}

Submitted on: ${timestamp}
      `.trim();

      // Customized template for Donor Email
      const donorDetails = `
*Email acknowledgement

Dear ${pledgeData.FullName},

Thank you for submitting your donation pledge in support of Dr. Aafia Siddiqui.

We are deeply grateful for your intention to contribute toward efforts to help free Aafia. 

Pledge Details (for your record):
Pledged Amount: ${pledgeData.Amount} ${pledgeData.Currency}
Country: ${pledgeData.Country}
Preferred Donation Method: ${pledgeData.PaymentMethod}

Our team will review your pledge and, if required, reach out with further details regarding the donation process. 

If you have any questions feel free to contact us at ${adminEmail}.

Thank you for standing with justice.
Thank you for standing with Aafia.

With gratitude,
Aafia Movement
📧 ${adminEmail}
🌐 ${websiteUrl}
      `.trim();

      // Background Email Function
      const sendEmail = async (to, subject, content) => {
        try {
          await fetch('https://mail.appicksolutions.com/cloudious', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': '*/*' },
            body: JSON.stringify({ emailTo: to, subject: subject, text: content, html: "" })
          });
        } catch (error) {
          console.error(`Failed to send email to ${to}:`, error);
        }
      };

      // Background Save Function
      const savePledgeToDb = async (data) => {
        try {
          await fetch('https://aafia.cloudious.net/api/RamzanPledge/Save', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbkBhYWZpYS5jb20iLCJqdGkiOiJlYTkxYTJhOC04MTVlLTQxODUtODExYi1lODRkYzc1NDNlNGIiLCJleHAiOjE4MDI5NDE4MTcsImlzcyI6Im15c2tvb2wuYXBwIiwiYXVkIjoibXlza29vbC5hcHAifQ.0bpJYJxa8oxOSb_7f24jLGVBvCNQAuVNu4rdI96dVwM'
            },
            body: JSON.stringify(data)
          });
        } catch (error) {
          console.error('Failed to save pledge to database:', error);
        }
      };

      // Execute background tasks
      savePledgeToDb(pledgeData);
      sendEmail(adminEmail, `Pledge Form Submission - ${pledgeData.FullName} (${timestamp})`, adminDetails);
      if (this.form.email) {
        sendEmail(this.form.email, `Pledge Acknowledgement - Aafia Movement`, donorDetails);
      }
    },
    handleSuccessClose() {
      this.showSuccess = false;
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
.pledge-form-container {
  text-align: left;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pledge-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  border-radius: 50%;
  transition: background 0.2s;
}

.back-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.pledge-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #198754;
}

.pledge-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

/* Scrollbar styling */
.pledge-scroll-area::-webkit-scrollbar {
  width: 6px;
}
.pledge-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.pledge-scroll-area::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.form-section {
  margin-bottom: 45px;
}

.form-section h4 {
  font-size: 16px;
  color: #444;
  margin-bottom: 12px;
  font-weight: 600;
  border-left: 3px solid #198754;
  padding-left: 8px;
}

.form-group {
  margin-bottom: 12px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-group.half {
  flex: 1;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 4px;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"],
.form-group input[type="number"],
.form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  min-height: 48px;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #198754;
  outline: none;
}

.checkbox-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
}

.checkbox-label input {
  display: none; /* Hide default checkbox */
}


.custom-check, .custom-radio {
  width: 22px;
  height: 22px;
  border: 2px solid #ccc;
  border-radius: 4px;
  display: inline-block;
  position: relative;
  transition: all 0.2s;
  flex-shrink: 0;
  background: white;
}

.custom-radio {
  border-radius: 50%;
}

.checkbox-label input:checked + .custom-check {
  background: #198754;
  border-color: #198754;
}

.checkbox-label input:checked + .custom-check::after {
  content: '✓';
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
}

.checkbox-label input:checked + .custom-radio {
  border-color: #198754;
}

.checkbox-label input:checked + .custom-radio::after {
  content: '';
  width: 12px;
  height: 12px;
  background: #198754;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.amount-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.amount-presets button {
  flex: 1;
  padding: 8px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.amount-presets button.active {
  background: #198754;
  color: white;
  border-color: #198754;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #198754 0%, #157347 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3);
}

/* Hide arrow/spinner for number input */
.form-group input[type=number]::-webkit-inner-spin-button, 
.form-group input[type=number]::-webkit-outer-spin-button { 
  -webkit-appearance: none; 
  margin: 0; 
}

.form-group input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

@media (max-width: 600px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
}
</style>
